import type { TFunction } from 'i18next';

import { LOL_PLAYERS_ROLE_ORDER } from 'app/config/videogameSpecific.config';
import { getStaticMapImage } from 'app/config/videogameSpecific.config.helper';
import { sort, sortAsModel } from 'app/utils/array';
import { padNumberToDisplay } from 'app/utils/number';
import { isNumber } from 'app/utils/types';
import { matchTypeAcronyms } from 'app/i18n/hardcoded_translations';

const getStatWinner = ({
  highlighted,
  team1Value,
  team2Value
}: Partial<StatLine<any>>): [boolean, boolean] => {
  if (isNumber(team1Value) && isNumber(team2Value) && highlighted && team1Value !== team2Value) {
    switch (highlighted) {
      case 'max':
        return Math.max(team1Value, team2Value) === team1Value ? [true, false] : [false, true];
      case 'min':
        return Math.min(team1Value, team2Value) === team1Value ? [true, false] : [false, true];
      default:
    }
  }
  return [false, false];
};

const formatGoldLead = (
  value: number,
  display: 'exact' | 'abbreviated',
  signed: boolean
): string => {
  const abs = Math.abs(value);
  const sign = signed ? '+' : '';
  if (display === 'exact') return `${sign}${abs}`;
  const k = abs / 1000;
  return `${sign}${Number.isInteger(k) ? k : k.toFixed(1)}k`;
};

const formatTimer = (timer: NonNullable<Timer['timer']>): string => {
  const totalSeconds = Math.round(timer);
  const abs = Math.abs(totalSeconds);
  const sign = totalSeconds < 0 ? '-' : '';
  return `${sign}${padNumberToDisplay(Math.floor(abs / 60))}:${padNumberToDisplay(abs % 60)}`;
};

const mapProperties = <T>(
  properties: StatProperties<T>[],
  opponentData: T | undefined | null
): StatData<T>[] =>
  properties.map(property => ({
    ...property,
    value: opponentData?.[property.name]
  }));

const mapPropertiesTeams = <T>(
  properties: StatProperties<T>[],
  opponent1Data: T | undefined,
  opponent2Data: T | undefined
): StatLine<T>[] =>
  properties.map(property => ({
    ...property,
    team1Value: opponent1Data?.[property.name],
    team2Value: opponent2Data?.[property.name]
  }));

const mapGaugesProperties = <T>(
  properties: StatProperties<T>[],
  opponent1Data: T | undefined,
  opponent2Data: T | undefined,
  filterUndefined = true
): GaugeStatLine<T>[] =>
  mapPropertiesTeams(properties, opponent1Data, opponent2Data).filter(
    value => !filterUndefined || isNumber(value.team1Value) || isNumber(value.team2Value)
  ) as GaugeStatLine<T>[];

const getPlayers = (
  opponent: OpponentWithStats | RosterOpponent | OpponentWithPlayersStats | undefined
): PlayerBase[] | Stats['players'] => {
  if (opponent) {
    if ('players' in opponent) {
      return opponent.players;
    }
    if ('stats' in opponent && opponent.stats && 'players' in opponent.stats) {
      return opponent.stats.players;
    }
  }
  return [];
};

const getSortedPlayers = (
  opponent: OpponentWithStats | RosterOpponent | OpponentWithPlayersStats | undefined
): (StatPlayer | PlayerBase | null)[] => {
  const players = getPlayers(opponent);

  if (Array.isArray(players)) {
    return sort(players, player => ('position' in player ? player.position : 0));
  }
  return sortAsModel(
    Object.entries(players),
    LOL_PLAYERS_ROLE_ORDER,
    playerWithRole => playerWithRole[0]
  ).map(playerWithRole => playerWithRole[1]);
};

const getPlayerBaseFromPlayerStat = (
  player: StatPlayer | undefined | null
): Partial<PlayerBase> => ({
  country: player?.country,
  id: player?.id,
  name: player?.name
});

const getMap = (game: MatchGame | undefined): GameMap | undefined => {
  if (!game) {
    return undefined;
  }
  const mapUrl = getStaticMapImage(game.videogame_slug);
  if (mapUrl) {
    return {
      id: 0,
      image_url: mapUrl,
      name: ''
    };
  }
  return game.map;
};

const getTeamNameAndAcronym = (
  team: OpponentBase | undefined,
  t: TFunction
): { name: string; acronym: string } => {
  const tbdName = t('all-team_name_tbd');
  const name = team ? team.name : tbdName;

  const acronym = team?.acronym ?? team?.name.slice(0, 3) ?? tbdName;

  return { name, acronym };
};

const getMatchType = ({
  match_type,
  number_of_games
}: {
  match_type?: MatchType;
  number_of_games?: number;
}): string =>
  match_type !== undefined && number_of_games !== undefined
    ? `${matchTypeAcronyms[match_type]} ${
        match_type === 'red_bull_home_ground' ? '' : number_of_games
      }`
    : '';

export {
  getStatWinner,
  formatGoldLead,
  formatTimer,
  mapPropertiesTeams,
  mapGaugesProperties,
  getSortedPlayers,
  getPlayerBaseFromPlayerStat,
  mapProperties,
  getMap,
  getTeamNameAndAcronym,
  getMatchType
};
